//noinspection JSUnresolvedVariable
import {Key} from './key';
import store from './store';
import actions from './actions';
import _remove from 'lodash/remove';
import _isEqual from 'lodash/isEqual';

//----------------------------------------------------------------------------------------------------------------------
// Loader

class Loader {
	constructor(parentKey, kind, observeEventPrefix, limit) {
		this.parentKey = parentKey;
		this.kind = kind;
		this.allLoaded = false;
		this.limit = limit;
		this.loading = false;
		this.iteration = 0;

		if (limit) {
			this.entities = [];
			store.observe(parentKey || kind, observeEventPrefix + '.add', (ev, eventParams) => {
				if (eventParams.kind === this.kind) {
					for (let child of eventParams.entities) {
						if (this.loading) {
							this.entities.push(child);
						} else {
							this.entities.unshift(child);
						}
					}
				}
			});
			store.observe(parentKey || kind, observeEventPrefix + '.remove', (ev, eventParams) => {
				if (eventParams.kind === this.kind) {
					this._forget(eventParams.entities);
				}
			});
		}
	}

	load(offset) {
		let promise, entities = null, loaded = false;

		if (this.limit) {
			if (this.entities.length >= offset + this.limit || this.allLoaded) {
				entities = this.entities.slice(offset, offset + this.limit);
				loaded = true;
			}
		} else if (this.allLoaded) {
			loaded = true;
		}

		if (loaded) {
			promise = Promise.resolve(this.limit ? [entities, offset + this.limit > this.entities.length] : entities);
		} else {
			this.loading = true;
			this.iteration += 1;
			const limit = this.iteration * this.limit;
			promise = this._fetch(limit, offset).then(data => {
				const children = this._store(data);
				this.allLoaded = !this.limit || children.length < limit;
				this.loading = false;
				return this.limit ? [children, this.allLoaded] : children;
			});
		}
		return promise;
	}

	/** Returns a Promise. */
	_fetch(limit, offset) {
		throw 'Not implemented';
	}

	/** Returns the new entities. */
	_store(rawEntities) {
		return rawEntities.map(rawEntity => store.put(rawEntity));
	}

	/** Removes the given key from the index */
	_forget(entities) {
		for (let entity of entities) {
			const entityKey = entity.key;
			_remove(this.entities, entity => entity.key.equals(entityKey));
		}
	}
}

class ChildrenLoader extends Loader {
	constructor(parentKey, kind, limit) {
		super(parentKey, kind, 'wow.children', limit);
	}

	_fetch(limit, offset) {
		return actions.get(this.parentKey, this.kind, null, limit, offset, ['-ctime']);
	}

	_store(rawEntities) {
		return store.put(this.parentKey, this.kind, rawEntities);
	}

	_forget(entities) {
		for (let entity of entities) {
			const entityId = entity.id;
			_remove(this.entities, entity => entity.id === entityId);
		}
	}
}

//----------------------------------------------------------------------------------------------------------------------
// Entity

export class Entity {
	constructor(parent, data) {
		this.parentKey = parent ? parent.key || parent : null;

		if (data) {
			// data may either contain an id or a key.
			// Keep only the id as we have the parent and can reconstruct the key
			// Do this before Object.assign to avoid conflict with getter
			let key = data.key;
			if (key) {
				data.id = key[key.length - 1];
				delete data.key;
			}

			if (data.parentKey) { // true when cloning
				delete data.parentKey;
			}

			// Transform dates
			let dateRegex = /.*(time|date)/;
			for (let prop in data) {
				if (dateRegex.test(prop)) {
					data[prop] = new Date(data[prop]);
				}
			}

			Object.assign(this, data);
		}
	}

	// Doesn't work because of minification
	// get kind() {
	// 	return this.__proto__.constructor.name;
	// }

	get key() {
		return new Key(this.parentKey, this.kind, this.id);
	}

	get parent() {
		return store.get(this.parentKey);
	}

	clone() {
		return new this.__proto__.constructor(this.parent, Object.assign({}, this));
	}

	addChildren(children) {
		let kind = children instanceof Array ? children[0].kind : children.kind;
		return actions.post(this.key, kind, children);
	}

	removeChildren(kind, ids, options) {
		return actions.delete(this.key, kind, ids, options);
	}

	loadChild(kind, id) {
		let promise;
		const key = this.key;
		let child = store.get(key, kind, id);
		if (child) {
			promise = Promise.resolve(child);
		} else {
			promise = actions.get(key, kind, id).then((data) => {
				return store.put(key, kind, data);
			});
		}
		return promise;
	}

	loadChildren(kind, limit, offset) {
		return this.getLoader(kind, limit).load(offset);
	}

	getLoader(kind, limit) {
		const key = this.key;
		let loader = store.childrenAttr(key, kind, 'loader');
		if (loader) {
			if (loader.limit !== limit) throw 'Invalid state';
		} else {
			loader = store.childrenAttr(key, kind, 'loader', new ChildrenLoader(key, kind, limit));
		}
		return loader;
	}

	update() {
		const stored = store.get(this.key).toJSON();
		let promise;
		if (_isEqual(stored, this.toJSON())) {
			promise = Promise.resolve(null);
		} else {
			promise = actions.put(this);
		}
		return promise;
	}

	toJSON() {
		const jentity = Object.assign({}, this);
		delete jentity.parentKey;
		return jentity;
	}

	equals(other) {
		return _isEqual(other, this);
	}
}

//----------------------------------------------------------------------------------------------------------------------
// Book

export class Book extends Entity {
	constructor(parent, data) {
		super(parent, data);
	}

	static createKey(id) {
		return new Key(['Book', id]);
	}

	getBooker(id) {
		return store.get(this.key, 'Booker', id);
	}

	get bookers() {
		return store.getByParent(this.key, 'Booker');
	}

	get invitations() {
		return store.getByParent(this.key, 'Invitation');
	}

	deleteInvitation(invitation) {
		return actions.delete(this.key, 'Invitation', invitation.id);
	}

	loadUsers() {
		let bookers = this.bookers;
		if (bookers.some(booker => booker.user_id && !store.get(null, 'User', booker.user_id))) {
			return actions.get(this.key, 'User').then(function (data) {
				store.put(null, 'User', data);
			}.bind(this));
		}
		return new Promise(function (resolve) {
			resolve();
		});
	}

	userBooker() {
		let booker = null;
		const userId = store.globals.user.id;
		for (let b of store.getByParent(this.key, 'Booker')) {
			if (b.user_id === userId) {
				booker = b;
				break;
			}
		}
		return booker;
	}
}

//----------------------------------------------------------------------------------------------------------------------
// Booker

export class Booker extends Entity {
	constructor(parent, data) {
		super(parent, data);
	}

	createInvitation(email) {
		return actions.post(this.key, 'Invitation', email);
	}

	isMyself() {
		const user = store.globals.user;
		return user && this.user_id === user.id;
	}

	get user() {
		return store.get(null, 'User', this.user_id);
	}
}

//----------------------------------------------------------------------------------------------------------------------
// Invitation

export class Invitation extends Entity {
	constructor(parent, data) {
		super(parent, data);
	}

	resend() {
		return actions.put(this);
	}
}

//----------------------------------------------------------------------------------------------------------------------
// Split

class Share {
	constructor(bookerId, splitValue) {
		this.id = bookerId;
		this.value = splitValue;
	}
}

function defineCachedProperty(properties, name, getter) {
	properties[name] = {
		get: function () {
			let symbol = Symbol.for(name);
			let value = this[symbol];
			if (value === undefined) {
				value = getter.apply(this, arguments);
				this[symbol] = value;
			}
			return value;
		}
	};
}

/** Warning: split is not mutable */
var defineSplitProperties = (function () {
	const splitProperties = (function () {
		let properties = {};

		defineCachedProperty(properties, 'totalUnits', function () {
			let totalUnits = 0;
			for (let share of this) {
				totalUnits += share.value;
			}
			return totalUnits;
		});

		/** Either "one" or "everybody" or "other" */
		defineCachedProperty(properties, 'type', function () {
			let oneUnits = 0, totalUnits = 0, peopleCount = 0;
			for (let share of this) {
				if (share.value === 1) {
					oneUnits += 1;
				}
				totalUnits += share.value;
				peopleCount += 1;
			}
			let type = "other";
			if (oneUnits === totalUnits) {
				if (totalUnits === peopleCount) {
					type = "everybody";
				} else if (totalUnits === 1) {
					type = "one";
				}
			}
			return type;
		});

		/** Id of the first paying booker of the sheet */
		defineCachedProperty(properties, 'bookerId', function () {
			for (let share of this) {
				if (share.value) {
					return share.id;
				}
			}
		});

		properties['idToShare'] = {
			get: function () {
				return this.reduce((map, share) => map.set(share.id, share.value), new Map());
			}
		};

		properties['valid'] = {
			get: function () {
				let sum = 0, valid = true;
				for (let share of this) {
					if (typeof share.value === 'number' && share.value >= 0) {
						sum += share.value;
					} else {
						valid = false;
						break;
					}
				}
				valid &= sum !== 0;
				return valid;
			}
		};

		return properties;
	}());

	return function (split) {
		Object.defineProperties(split, splitProperties);
	}
}());

export class HasSplit extends Entity {
	constructor(parent, data) {
		super(parent, data);
		this.postInit();
	}

	postInit() {
		if (this.split) {
			if (!this.split.type) {
				defineSplitProperties(this.split);
			}
		} else {
			this.split = null;
		}
		return this;
	}

	createSplit() {
		let split = this.bookers().map(booker => new entities.Share(booker.id, 1));
		defineSplitProperties(split);
		return split;
	}

	static copySplit(split) {
		let copy;
		if (split) {
			copy = [];
			for (let share of split) {
				copy.push(Object.assign({}, share));
			}
			defineSplitProperties(copy);
		} else {
			copy = null;
		}
		return copy;
	}

	cloneSplit() {
		let split = HasSplit.copySplit(this.split);
		if (split) {
			let idToShare = split.idToShare;
			for (let booker of this.bookers()) {
				if (!idToShare.has(booker.id)) {
					idToShare.set(booker.id, 0);
				}
			}
			if (idToShare.size !== split.length) {
				let newSplit = [];
				for (let [id, value] of idToShare) {
					newSplit.push(new Share(id, value));
				}
				split = newSplit;
			}
		}
		return split;
	}

	assignSplit(split) {
		this.split = HasSplit.copySplit(split);
	}

	clone() {
		let entity = super.clone();
		entity.split = this.cloneSplit();
		return entity;
	}
}

//----------------------------------------------------------------------------------------------------------------------
// Sheet

class SheetLoader extends Loader {
	constructor(limit) {
		super(null, 'Sheet', 'wow', limit);
		store.observe('Sheet', 'wow.update', (ev, evParams) => {
			if (evParams.kind === this.kind) {
				Sheet.sort(this.entities);
			}
		});
	}

	_fetch(limit, offset) {
		return actions.get(null, 'Sheet/mine', null, limit, offset);
	}
}

var sheetLoader = null;

export class Sheet extends HasSplit {
	constructor(parent, data) {
		super(parent, Object.assign({
			name: '',
			state: 0
		}, data));
	}

	get sheeters() {
		return store.getByParent(this.key, 'Sheeter');
	}

	bookersIds() {
		return this.balance.map(balance => balance.id);
	}

	bookers() {
		return store.getByParent(this.parentKey, 'Booker', this.bookersIds());
	}

	getBooker(id) {
		return store.get(this.parentKey, 'Booker', id);
	}

	get currency() {
		return this.parent.currency;
	}

	rows() {
		return store.getByParent(this.key, 'Row');
	}

	get isOpen() {
		return this.state === 0;
	}

	get closed() {
		return this.state !== 0;
	}

	myBooker() {
		return this.bookers().find(b => b.isMyself());
	}

	load() {
		return this.parent.loadChildren('Booker')
			.then(() => this.loadChildren('Sheeter'))
			.then(() => this);
	}

	static getMineLoader() {
		const limit = 10;
		if (sheetLoader) {
			if (sheetLoader.limit !== limit) throw 'Invalid state';
		} else {
			sheetLoader = new SheetLoader(limit);
		}
		return sheetLoader;
	}

	static sort(sheets) {
		return sheets.sort((a, b) => {
			let d = a.state - b.state;
			if (d === 0) {
				d = b.mtime - a.mtime;
			}
			return d;
		});
	}
}

//----------------------------------------------------------------------------------------------------------------------
// Sheeter

let openSheetsLoaded = false;

export class Sheeter extends Entity {
	constructor(parent, data) {
		super(parent, data);
	}

	get booker() {
		return store.get(this.parentKey.parent, 'Booker', this.booker_id);
	}
}

//----------------------------------------------------------------------------------------------------------------------
// Row

export class Row extends HasSplit {
	constructor(parent, data) {
		super(parent, data);
		if (!this.amount) {
			this.amount = 0;
		}
	}

	get payorId() {
		return this.payor_id;
	}

	set payorId(payorId) {
		this.payor_id = Number(payorId);
	}

	get booker() {
		return this.getBooker(this.payor_id);
	}

	getBooker(id) {
		return store.get(this.parentKey.parent, 'Booker', id);
	}

	bookers() {
		return this.parent.bookers();
	}
}

//----------------------------------------------------------------------------------------------------------------------
// Transaction

export class Transaction extends Entity {
	originKey() {
		return this.origin_entity ? new Key(this.origin_entity) : null;
	}

	debtorBooker() {
		return store.get(this.parentKey, 'Booker', this.debtor_id);
	}

	creditorBooker() {
		return store.get(this.parentKey, 'Booker', this.creditor_id);
	}

	isFromSheet() {
		const originEvent = this.origin_event;
		return originEvent === 'close' || originEvent === 'open';
	}

	isPayback() {
		return this.origin_event === 'payback';
	}
}

//----------------------------------------------------------------------------------------------------------------------
// User

export class User extends Entity {

}

//----------------------------------------------------------------------------------------------------------------------
// Export

var entities = {
	User, Book, Booker, Invitation, Sheet, Sheeter, Row, Share, Transaction
};

for (let kind of Object.keys(entities)) {
	entities[kind].prototype.kind = kind;
}

export function create(parentKey, kind, data) {
	return new entities[kind](parentKey, data);
}




// WEBPACK FOOTER //
// ./src/core/entities.js